import React, { useState } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DrillPanel from './DrillPanel';
import { makeInfluenceDrillConfig, influenceDrillColumns } from './drillConfigs';
import { PERSON_DEALS_LABEL, DIRECT_COLUMN_LABEL, DIRECT_LEGEND, DIRECT_TOOLTIP } from './personDealsText';

// Plan 2217 stream B: the deals behind one person = the influenced deals created in the period on the account(s) the person belongs
// to. `/exec/employees` (influenced basis) adds `account_link` and `influenced_deals` to each row; `/exec/influence/deals?of=person:<email>`
// lists them. Nobody gets a share of a deal: totals do not add across people.

export const NOT_ON_TEAM = 'Not on an opportunity team';
export const NO_LINK = 'No account link yet';

const viaPerson = (v) => (v == null ? '—' : v ? 'Yes' : 'No');

export const makePersonDealsConfig = (email, scope = {}) => {
  const base = makeInfluenceDrillConfig(`person:${email}`, { ...scope, surface: 'participant' }, { amountKind: 'deal', amountLabel: 'Deal $' });
  return {
    ...base,
    columns: [
      ...influenceDrillColumns('deal', 'Deal $'),
      { id: 'credited_directly', label: DIRECT_COLUMN_LABEL, width: 150, align: 'center', format: viaPerson, tooltip: DIRECT_TOOLTIP },
      { id: 'via_person', label: 'Via workshops this person attended', width: 150, align: 'center', format: viaPerson,
        tooltip: "Yes when one of the workshops or events that credit this deal is one this person checked in to. No means the deal sits on their account but their own attendance is not behind it." },
    ],
    disclaimer: `${PERSON_DEALS_LABEL} ${DIRECT_LEGEND}`,
  };
};

// "Deals shown for X of Y people" from the rows on screen: a blank is "no account link", never "no influence".
export const personDealsCoverage = (rows) => {
  const people = (rows || []).filter((r) => r && r.account_link);
  if (!people.length) return null;
  const shown = people.filter((r) => r.account_link.resolved && r.influenced_deals?.n_deals != null).length;
  const reloading = people.filter((r) => r.account_link.resolved && r.influenced_deals?.n_deals == null).length;
  const head = `Deals shown for ${shown} of ${people.length} people; `;
  const unlinked = 'have no account link yet (most internal attendees are on no opportunity team; external domains await Domain Matches review)';
  const counted = people.filter((r) => r.influenced_deals?.n_direct != null && r.influenced_deals.n_deals > 0);
  const direct = counted.length ? ` ${counted.filter((r) => r.influenced_deals.n_direct > 0).length} of the ${counted.length} with deals have at least one deal credited to them directly.` : '';
  return (reloading ? `${head}${people.length - shown - reloading} ${unlinked}; ${reloading} are reloading.` : `${head}the rest ${unlinked}.`) + direct;
};

// The list column holds this text so CSV / Excel / PDF exports carry the count or the reason, not an object.
export const dealsLabel = (r) => {
  const l = r.account_link;
  if (!l) return '';
  if (!l.resolved) return l.reason === 'not_on_opportunity_team' ? NOT_ON_TEAM : NO_LINK;
  const d = r.influenced_deals;
  if (d?.n_deals == null) return 'Reloading';
  return d.n_direct == null ? String(d.n_deals) : `${d.n_deals} influenced ${d.n_deals === 1 ? 'deal' : 'deals'}, ${d.n_direct} credited directly`;
};

export function PersonDealsCell({ row, ctx }) {
  const [open, setOpen] = useState(false);
  const link = row?.account_link;
  if (!link) return '—';
  if (!link.resolved) {
    return (
      <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }} title={link.message || undefined} data-testid="person-deals-none">
        {link.reason === 'not_on_opportunity_team' ? NOT_ON_TEAM : NO_LINK}
      </Typography>
    );
  }
  if (row.influenced_deals?.n_deals == null) {
    return <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>Reloading</Typography>;
  }
  const n = row.influenced_deals.n_deals;
  const m = row.influenced_deals.n_direct;
  return (
    <>
      <Button size="small" variant="text" onClick={() => setOpen(true)} sx={{ fontSize: '0.72rem', minWidth: 0, p: 0, textTransform: 'none' }}
              aria-label={`Open the ${n} influenced deals on the account of ${row.fullname || row.email}`} data-testid="person-deals-link">
        {n.toLocaleString()} {n === 1 ? 'deal' : 'deals'}
      </Button>
      {m != null && (
        <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }} data-testid="person-deals-direct"
                    title="Deals where this person is on the deal's team or contacts and attended TEC by the deal's end date. The other deals are only on their account.">
          , {m.toLocaleString()} credited directly
        </Typography>
      )}
      {open && (
        <Dialog open onClose={() => setOpen(false)} onClick={(e) => e.stopPropagation()} maxWidth="xl" fullWidth aria-label="Influenced deals on this person's accounts">
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', fontSize: '1rem' }}>
            <Box sx={{ flexGrow: 1 }}>Influenced deals — {row.fullname || row.email}</Box>
            <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close"><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          <DialogContent>
            <DrillPanel title="Influenced deals on this person's account(s)" startDate={ctx?.startDate} endDate={ctx?.endDate}
                        panelConfig={makePersonDealsConfig(row.email, { startDate: ctx?.startDate, endDate: ctx?.endDate })} hideNewTab />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
